import { useEffect, useState } from "react";
import { Alert, Button, Container } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faHome,
  faHourglassHalf,
  faInfoCircle,
  faPlane,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import Header from "../Header";
import { CrewJoiner } from "./CrewJoiner";
import {
  InProgress,
  JoinCrewState,
  JoiningError,
  SuccessfullyJoined,
} from "./JoinCrewState";
import SwitchUtils from "../common/SwitchUtils";
import { useOnlineOffline } from "../common/hooks/useOnlineOffline";
import { CrewJoinerTexts } from "./CrewJoinerTexts";
import { CyCrewJoiner } from "./CyCrewJoiner";
import { useClient } from "urql";
import { useCrewsQuery } from "./useCrewsQuery";
import { renderLoadingUntilDefined } from "../common/LoadingUntilDefined";
import { useTranslation } from "react-i18next";

interface Props {
  joinIdentifier: string;
}

export function JoinCrewDialog({ joinIdentifier }: Props) {
  const { t } = useTranslation();
  const isOnline = useOnlineOffline();

  const { crews } = useCrewsQuery();

  const [joinCrewState, setJoinCrewState] = useState<JoinCrewState>(
    new InProgress(),
  );

  const client = useClient();

  useEffect(() => {
    if (!isOnline) {
      return;
    }
    if (crews === undefined) {
      return;
    }

    const maybeCrew = crews.find((i) => i.joiningIdentifier === joinIdentifier);
    if (maybeCrew) {
      console.info(
        "User already belongs to crew " +
          maybeCrew.title +
          ". Redirecting to /.",
      );
      window.location.href = "/";
      return;
    }

    const crewJoiner = new CrewJoiner(client);
    crewJoiner
      .joinCrew(joinIdentifier)
      .then((title) => {
        setJoinCrewState(new SuccessfullyJoined(title));
        console.info("User joined crew=" + title);
      })
      .catch((err: unknown) => {
        const message =
          "Error while joining crew with joiningIdentifier=" + joinIdentifier;
        console.error(message, err);
        setJoinCrewState(new JoiningError(message));
        return Promise.resolve();
      });
  }, [isOnline, crews, joinIdentifier, client]);

  return (
    <Container fluid>
      <Header />
      <br />
      {renderLoadingUntilDefined(crews, () => {
        return renderState();
      })}
    </Container>
  );

  function renderState() {
    if (!isOnline) {
      return renderNoInternet();
    }

    switch (joinCrewState.state) {
      case "InProgress":
        return renderInProgress();
      case "SuccessfullyJoined":
        return renderJoiningSuccessful(joinCrewState.title);
      case "JoiningError":
        return renderJoiningError(joinCrewState.errorMessage);
      default:
        return SwitchUtils.throwUnsupportedValue(joinCrewState);
    }
  }

  function renderNoInternet() {
    return (
      <Alert color="danger" fade={false}>
        <h2 className={"light-header"}>
          <FontAwesomeIcon icon={faPlane} /> {t("crew.join.noInternetTitle")}
        </h2>
        <span>{t("crew.join.noInternetDesc")}</span>
        <br />
        <br />
        <Link to="/">
          <FontAwesomeIcon icon={faHome} /> {t("crew.join.backToHome")}
        </Link>
      </Alert>
    );
  }

  function renderInProgress() {
    return (
      <Alert color="primary" fade={false}>
        <h2 className={"light-header"}>
          <FontAwesomeIcon icon={faHourglassHalf} />{" "}
          {t("crew.join.inProgressTitle")}
        </h2>
        {t("crew.join.inProgressDesc")}
      </Alert>
    );
  }

  function renderJoiningSuccessful(title: string) {
    return (
      <Alert color="success" data-cy={CyCrewJoiner.AlertSuccess} fade={false}>
        <h2 className={"light-header"}>
          <FontAwesomeIcon icon={faInfoCircle} />{" "}
          {CrewJoinerTexts.JoiningSuccessful}
        </h2>
        <span>{t("crew.join.successDesc", { title })}</span>
        <br />
        <br />

        <Button
          color="success"
          data-cy={CyCrewJoiner.ButtonOk}
          onClick={() => {
            //Not only using Reacts router but really reload the page to refetch the crews!
            window.location.href = window.location.origin;
          }}
        >
          {t("crew.join.ok")}
        </Button>
      </Alert>
    );
  }

  function renderJoiningError(errorMessage: string) {
    return (
      <Alert color="danger" fade={false}>
        <h2 className={"light-header"}>
          <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
          {t("crew.join.errorTitle")}
        </h2>
        <span>
          {t("crew.join.errorDesc")}
          <br />
          {t("crew.join.techError", { error: errorMessage })}
        </span>
        <br />
        <br />
        <Link to="/">
          <FontAwesomeIcon icon={faHome} /> {t("crew.join.home")}
        </Link>
      </Alert>
    );
  }
}
